import type { TFunction } from 'i18next'

import type { Campaign, CampaignType } from '@/ducks/campaigns/campaigns.types'

export type CampaignDisplayStatus = 'active' | 'inactive' | 'expired'

const MS_PER_DAY = 24 * 60 * 60 * 1000

const parseExpiryToTimestamp = (value: string | number | null | undefined): number | null => {
  if (value == null || value === '') return null

  if (typeof value === 'number') {
    return value > 0 ? value : null
  }

  const numeric = Number(value)
  if (!Number.isNaN(numeric) && numeric > 0) {
    return numeric
  }

  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

export const getCampaignExpiryTimestamp = (campaign: Campaign): number | null => {
  if (campaign.campaignType === 'food-offer') {
    return (
      parseExpiryToTimestamp(campaign.validUntil) ??
      parseExpiryToTimestamp(campaign.expiryDate) ??
      parseExpiryToTimestamp(campaign.offerValidity)
    )
  }

  if (campaign.campaignType === 'cart_discount') {
    return (
      parseExpiryToTimestamp(campaign.expiryDate) ??
      parseExpiryToTimestamp(campaign.expirationTime)
    )
  }

  return parseExpiryToTimestamp(campaign.expiryDate)
}

export const isCampaignExpired = (campaign: Campaign, now = Date.now()): boolean => {
  const expiry = getCampaignExpiryTimestamp(campaign)
  if (expiry == null) return false
  return expiry < now
}

export const getCampaignDisplayStatus = (
  campaign: Campaign,
  now = Date.now()
): CampaignDisplayStatus => {
  if (isCampaignExpired(campaign, now)) return 'expired'
  if (campaign.status === 1) return 'active'
  return 'inactive'
}

export const getCampaignOfferLabel = (
  campaign: Campaign,
  t: TFunction
): string => getCampaignTypeLabel(campaign.campaignType, t)

export const getCampaignPercentageBadge = (
  campaign: Campaign,
  t: TFunction
): string | null => {
  if (campaign.campaignType === 'cart_discount' && campaign.amountPercentage != null) {
    return t('marketing.campaigns.percent-off', {
      value: campaign.amountPercentage
    })
  }

  if (campaign.campaignType === 'food-offer' && campaign.offerAmount != null) {
    return t('marketing.campaigns.percent-off', {
      value: campaign.offerAmount
    })
  }

  return null
}

export const getCampaignFoundedBy = (campaign: Campaign): string | null => {
  const record = campaign as Campaign & {
    fundedBy?: string | null
    founded_by?: string | null
  }
  const value = campaign.foundedBy ?? record.fundedBy ?? record.founded_by

  return value?.trim() || null
}

export const getFundedByLabel = (
  foundedBy: string | null,
  t: TFunction
): string | null => {
  if (!foundedBy?.trim()) return null
  return t('marketing.campaigns.funded-by', { name: foundedBy.trim() })
}

export const getCampaignFundedByLabel = (
  campaign: Campaign,
  t: TFunction
): string => {
  const foundedBy = getCampaignFoundedBy(campaign)
  if (!foundedBy) return '--'
  return t('marketing.campaigns.funded-by', { name: foundedBy })
}

export const getCampaignExpirationText = (
  campaign: Campaign,
  t: TFunction,
  now = Date.now()
): string => {
  const expiry = getCampaignExpiryTimestamp(campaign)

  if (expiry == null || expiry < now) {
    return '--'
  }

  const daysLeft = Math.max(1, Math.ceil((expiry - now) / MS_PER_DAY))
  return t('marketing.campaigns.days-left', { count: daysLeft })
}

export const getCampaignTypeLabel = (
  campaignType: CampaignType,
  t: TFunction
): string => {
  const keyMap: Record<CampaignType, string> = {
    'food-offer': 'marketing.campaigns.types.food-offer',
    cart_discount: 'marketing.campaigns.types.cart-discount',
    buyXGetYOffer: 'marketing.campaigns.types.buy-x-get-y-default'
  }

  return t(keyMap[campaignType])
}
